<template>
  <div class="w-full h-full overflow-hidden rounded-lg bg-light-box-background dark:bg-dark-box-background">
    <div
      v-if="expansion_list == 'all_lists'"
      class="text-xl font-medium text-light-body-text dark:text-dark-body-text flex pl-3 h-[48px] items-center py-3 justify-between">
      {{ label }}
    </div>

    <div v-if="lists.length > 0" class="flex pl-3 items-center py-3 justify-between h-[64px]">
      <div v-if="!expansion_list || expansion_list == 'all_lists'" class="flex">
        <SelectInput
          name="List"
          classes="w-56"
          :show-label="true"
          :allow-null="false"
          :items="lists"
          label-attr="name"
          :value="selected_list?.id"
          value-attr="id"
          :return-ids="false"
          :is-box-background="true"
          :label="$t('List')"
          input-classes="text-left"
          @update:currentItem="($event) => getList($event)" />
        <button
          v-if="selected_list && !expansion_list"
          :class="[{ 'has-tooltip': editing_disabled }]"
          :disabled="editing_disabled"
          class="cancel-btn truncate inline-flex ml-1"
          @click="editing_disabled ? null : $emitter.emit(ALL_EMITTERS.DELETE_EXPANSION_LIST, selected_list)">
          {{ $t("Delete list") }}
          <span v-if="editing_disabled"
                class="flex-wrap p-2 bg-gray-300 rounded tooltip whitespace-nowrap mt-7">{{ $t("You don't have access to this feature") }}
          </span>

          <Trash2 class="ml-2 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
        </button>
      </div>
      <div v-else class="text-xl font-medium text-light-body-text dark:text-dark-body-text">
        {{ label }}
      </div>

      <!-- probably needs to be bound to selected_list -->
      <div v-if="selected_list && !editing_disabled" class="flex items-center z-30 absolute right-5">
        <div class="px-2">
          <RecordActionsDropDown
            :box-background="true"
            :icon-button="true"
            :component-reference="componentReference"
            :selected-records="selected_records"
            :selected-full-records="selected_full_records"
            :object-type="selected_list.type"
            :page-reference="PAGE_REFERENCES.PROSPECTING_EXPANSION_LISTS" />
        </div>
        <div
          class="w-10 h-10 border hover:cursor-pointer bg-white shadow-none dark:bg-dark-screen-background inline-flex items-center justify-center text-gray-400 rounded border-light-divider-border dark:border-dark-divider-border transition ease-in-out duration-side-menu-transition-duration"
          @click="updateMetrics">
          <Grid2x2Plus class="w-5 h-5 text-gray-500 hover:text-gray-700" aria-hidden="true" />
        </div>
      </div>
    </div>
    <div v-else class="bg-white dark:bg-dark-box-background flex justify-center min-h-[500px] overflow-hidden rounded-lg p-5">
      <div class="flex justify-center items-center flex-col">
        <Info class="h-20 w-20 text-blue" aria-hidden="true" />
        <h3 class="mt-2 text-xl font-normal text-black dark:text-white">
          {{ $t("No lists found") }}
        </h3>
        <p class="mt-2 text-md font-normal text-gray-500">
          {{ $t("You don't have any saved Lists right now. You can easily add any customer or contact to a list by using Quick Actions.") }}
        </p>
      </div>
    </div>

    <div
      v-if="selected_list"
      class="relative z-1"
      :class="[expansion_list == 'all_lists' ? 'h-[calc(100%-112px)]' : 'h-[calc(100%-64px)]']">
      <AdvancedCompaniesMetricsTable
        v-model:selected-records="selected_records"
        v-model:selected-full-records="selected_full_records"
        v-model:records="records"
        :component-reference="componentReference"
        :initial-records="initial_records"
        :show-filters="false"
        :object-type="selected_list.type"
        :page-reference="PAGE_REFERENCES.PROSPECTING_EXPANSION_LISTS"
        :adjustable="true" />
    </div>
  </div>
</template>

<script>
import AdvancedCompaniesMetricsTable from "@/components/shared/AdvancedCompaniesMetricsTable.vue"
import dataService from "@/services/data.service"
import { Grid2x2Plus, Trash2, Info } from "lucide-vue-next"
import SelectInput from "@/components/shared/SelectInput.vue"
import RecordActionsDropDown from "@/components/shared/RecordActionsDropDown.vue"
import { VIEW_TYPES, PAGE_REFERENCES } from "@/store/constants/references"

import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Info,
    AdvancedCompaniesMetricsTable,
    RecordActionsDropDown,
    Grid2x2Plus,
    Trash2,
    SelectInput
  },

  inject: ["canUserEditLayout"],
  props: {
    label: {
      type: String,
      default: ""
    },
    componentReference: {
      type: String,
      required: false
    },
    expansion_list: {
      type: Object,
      default: null
    }
  },
  emits: ["update:selectedRecords", "update:selectedFullRecords"],
  data() {
    return {
      selected_list: null,
      records: [],
      lists: [],
      selected_records: [],
      selected_full_records: [],
      initial_records: [],
      PAGE_REFERENCES,
      ALL_EMITTERS
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },

  watch: {
    selected_records() {
      if (this.selected_records.length) {
        this.$emit("update:selectedRecords", this.selected_records) // for PageView RollupMetricsGrid
        this.$emitter.emit(this.componentReference, {
          event: "selectedRecords",
          data: this.selected_records
        })
      } else {
        let records = this.initial_records?.results?.map((r) => r.id)
        this.$emit("update:selectedRecords", records) // for PageView RollupMetricsGrid
        this.$emitter.emit(this.componentReference, {
          event: "selectedRecords",
          data: records
        })
      }
    },
    selected_full_records() {
      this.$emit("update:selectedFullRecords", this.selected_full_records) // for assign owner/co-owners
    }
  },
  created() {
    this.register_listeners()
    dataService.getExpansionLists().then((result) => {
      this.lists = result.data.results
      if (this.expansion_list && this.expansion_list != "all_lists") {
        this.getList(this.expansion_list)
      } else if (this.lists.length > 0) {
        this.getList(this.lists[0])
      }
    })
  },
  activated() {
    this.register_listeners()

    dataService.getExpansionLists().then((result) => {
      this.lists = result.data.results
      if (this.expansion_list && this.expansion_list != "all_lists") {
        this.getList(this.expansion_list)
      } else if (this.lists.length > 0) {
        this.getList(this.lists[0])
      }
    })
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.DELETE_EXPANSION_LIST_IN_EXPANSION_LIST_COMPONENT, (list) => {
        this.deleteList(list)
      })
      this.$emitter.on(ALL_EMITTERS.GET_LIST_IN_EXPANSION_LIST_COMPONENT, (list) => {
        this.getList(list)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.DELETE_EXPANSION_LIST_IN_EXPANSION_LIST_COMPONENT)
      this.$emitter.off(ALL_EMITTERS.GET_LIST_IN_EXPANSION_LIST_COMPONENT)
    },
    updateMetrics() {
      const metrics_obj = {
        page_reference: PAGE_REFERENCES.PROSPECTING_EXPANSION_LISTS,
        object_type: this.selected_list.type,
        view_type: VIEW_TYPES.TABLE
      }
      if (this.$route.params?.page) {
        metrics_obj["layout_page_reference"] = this.$route.params?.page
      }
      if (this.componentReference) {
        metrics_obj["component_reference"] = this.componentReference
      }

      this.$emitter.emit(ALL_EMITTERS.UPDATE_METRICS, metrics_obj)
    },
    getList(list) {
      this.selected_records = []
      this.$emitter.emit(ALL_EMITTERS.SELECTED_EXPANSION_LIST, this.selected_list)
      dataService.getExpansionList(list?.id ? list.id : list).then((result) => {
        this.selected_list = result.data
        this.initial_records = result.data.records

        this.records = result.data.records.results.map((x) => x.id)
        this.$emit("update:selectedRecords", this.records) // for PageView RollupMetricsGrid
      })
    },

    deleteList(list) {
      dataService
        .deleteExpansionList(list.id)
        .then(() => {
          let index = this.lists.findIndex((x) => x["id"] == list.id)
          if (index != -1) {
            this.lists.splice(index, 1)
            if (this.lists.length > 0) {
              this.getList(this.lists[0])
            } else {
              this.selected_list = null
            }
          }
        })
        .catch(() => {
          dataService.getExpansionLists().then((result) => {
            this.lists = result.data.results
            if (this.lists.length > 0) {
              this.getList(this.lists[0])
            }
          })
        })
    }
  }
}
</script>
